import {
  Box,
  Text,
  Button,
  Stack,
  styled,
  Tabs,
  Tab,
  Input,
  alpha,
} from '@ltvco/refresh-lib/theme';

export const PopoverContainer = styled(Box)(({ theme }) => ({
  width: '560px',
  height: '580px', // Fixed height for consistent sizing
  display: 'flex',
  flexDirection: 'column',
  [theme.breakpoints.down('sm')]: {
    width: '100vw',
    maxWidth: '100vw',
    height: '85vh',
    maxHeight: '85vh',
  },
}));

export const Header = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(2, 2.5),
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

export const Title = styled(Text)(() => ({
  fontWeight: 600,
  fontSize: '1.125rem',
}));

export const TabsContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  borderBottom: `1px solid ${theme.palette.divider}`,
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    right: 0,
    height: '100%',
    width: '24px',
    background: `linear-gradient(to right, ${alpha(
      theme.palette.common.white,
      0
    )}, ${alpha(theme.palette.common.white, 0.9)})`,
    pointerEvents: 'none',
    zIndex: 1,
    [theme.breakpoints.up('md')]: {
      display: 'none',
    },
  },
}));

export const StyledTabs = styled(Tabs)(({ theme }) => ({
  minHeight: '44px',
  '& .MuiTabs-flexContainer': {
    gap: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
      gap: theme.spacing(0.5),
      width: 'max-content',
    },
  },
  '& .MuiTabs-indicator': {
    height: '3px',
    borderRadius: '2px 2px 0 0',
  },
  '& .MuiTabs-scrollButtons': {
    display: 'none',
  },
  [theme.breakpoints.down('sm')]: {
    '& .MuiTabs-scroller': {
      overflow: 'auto !important',
      scrollbarWidth: 'none',
      '&::-webkit-scrollbar': {
        display: 'none',
      },
    },
  },
}));

export const StyledTab = styled(Tab)(({ theme }) => ({
  textTransform: 'none',
  fontWeight: 500,
  fontSize: '0.875rem',
  minHeight: '44px',
  padding: theme.spacing(1, 1.5),
  borderRadius: '8px 8px 0 0',
  transition: 'all 0.2s ease-in-out',
  '&.Mui-selected': {
    fontWeight: 600,
    color: theme.palette.primary.main,
  },
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.8125rem',
    minHeight: '40px',
    minWidth: '70px',
    padding: theme.spacing(1),
  },
}));

export const TabScrollIndicator = styled(Box)(({ theme }) => ({
  display: 'flex',
  position: 'absolute',
  right: 0,
  top: '50%',
  transform: 'translateY(-50%)',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: alpha(theme.palette.common.white, 0.8),
  borderRadius: '50%',
  width: '24px',
  height: '24px',
  zIndex: 2,
  boxShadow: `0 2px 4px ${alpha(theme.palette.common.black, 0.1)}`,
  border: '1px solid',
  borderColor: theme.palette.divider,
  [theme.breakpoints.up('md')]: {
    display: 'none',
  },
}));

export const ContentBody = styled(Box)(({ theme }) => ({
  flex: 1,
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0, // Important for flex child scrolling
  padding: theme.spacing(2, 2.5),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1.5, 2),
  },
}));

export const SearchInputContainer = styled(Stack)(({ theme }) => ({
  position: 'relative',
  flexDirection: 'row',
  alignItems: 'center',
  backgroundColor: theme.palette.background.paper,
  borderRadius: '24px',
  border: `1px solid ${theme.palette.divider}`,
  overflow: 'hidden',
  marginBottom: theme.spacing(2),
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    borderColor: theme.palette.primary.main,
  },
  '&:focus-within': {
    borderColor: theme.palette.primary.main,
    boxShadow: `0 0 0 2px ${theme.palette.primary.main}20`,
  },
}));

export const SearchInput = styled(Input)(({ theme }) => ({
  flex: 1,
  '& .MuiInputBase-root': {
    overflow: 'hidden',
    '&:before, &:after': {
      display: 'none',
    },
  },
  '& .MuiInputBase-input': {
    padding: '10px 6px 10px 2px',
    fontSize: '0.875rem',
    '&::placeholder': {
      color: theme.palette.text.secondary,
      opacity: 1,
    },
  },
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
}));

export const SearchIconContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(0, 1),
  display: 'flex',
  alignItems: 'center',
  color: theme.palette.text.secondary,
}));

export const ControlsRow = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: theme.spacing(1.5),
}));

export const ClearAllButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  fontWeight: 500,
  fontSize: '0.875rem',
  color: theme.palette.primary.main,
  padding: 0,
  minWidth: 'auto',
  '&:hover': {
    backgroundColor: 'transparent',
    textDecoration: 'underline',
  },
}));

export const JurisdictionsList = styled(Box)(({ theme }) => ({
  flex: 1,
  overflow: 'auto',
  display: 'grid',
  gridTemplateColumns: 'repeat(2, 1fr)',
  gap: 0,
  alignContent: 'flex-start',
  minHeight: 0, // Important for flex child scrolling
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: '1fr',
  },
  '&::-webkit-scrollbar': {
    width: '6px',
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: theme.palette.action.hover,
    borderRadius: '3px',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: theme.palette.action.disabled,
    borderRadius: '3px',
    '&:hover': {
      backgroundColor: theme.palette.action.active,
    },
  },
}));

export const JurisdictionItem = styled(Stack)(({ theme }) => ({
  alignItems: 'center',
  padding: theme.spacing(0.25, 0.5),
  borderRadius: '4px',
  transition: 'background-color 0.1s ease',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  '& .MuiCheckbox-root': {
    padding: theme.spacing(0.25),
    marginRight: theme.spacing(0.5),
  },
  '& .MuiFormControlLabel-root': {
    cursor: 'pointer',
    margin: 0,
    width: '100%',
    display: 'flex',
  },
}));

export const Footer = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(2, 2.5),
  borderTop: `1px solid ${theme.palette.divider}`,
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.5, 2),
  },
}));

export const SelectedCount = styled(Text)(({ theme }) => ({
  fontSize: '0.875rem',
  color: theme.palette.text.secondary,
  [theme.breakpoints.down('sm')]: {
    textAlign: 'center',
  },
}));

export const FooterButtons = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  gap: theme.spacing(1.5),
  [theme.breakpoints.down('sm')]: {
    width: '100%',
    flexDirection: 'column-reverse',
    gap: theme.spacing(1),
  },
}));

export const CancelButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  fontWeight: 500,
  borderRadius: '24px',
  padding: theme.spacing(1, 3),
  borderColor: theme.palette.divider,
  color: theme.palette.text.primary,
  [theme.breakpoints.down('sm')]: {
    width: '100%',
  },
}));

export const ApplyButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  fontWeight: 600,
  borderRadius: '24px',
  padding: theme.spacing(1, 3),
  [theme.breakpoints.down('sm')]: {
    width: '100%',
  },
}));

export const EmptyState = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(4),
  textAlign: 'center',
  color: theme.palette.text.secondary,
  gridColumn: '1 / -1',
  minHeight: '150px',
}));
